import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import api from '../lib/api';
import Confetti from 'react-confetti';
import GoldButton from '../components/GoldButton';
import { Trophy, Calendar, Bike, Palette, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';

export default function ClienteSuccess() {
  const { cedula } = useParams();
  const navigate = useNavigate();
  const [beneficiario, setBeneficiario] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showConfetti, setShowConfetti] = useState(true);
  const [timeRemaining, setTimeRemaining] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0
  });

  useEffect(() => {
    fetchBeneficiario();
  }, [cedula]);

  useEffect(() => {
    if (!beneficiario?.fecha_entrega) return;

    const interval = setInterval(() => {
      const now = new Date();
      const deliveryDate = new Date(beneficiario.fecha_entrega);
      const difference = deliveryDate - now;

      if (difference <= 0) {
        setTimeRemaining({ days: 0, hours: 0, minutes: 0, seconds: 0 });
        clearInterval(interval);
        return;
      }

      const days = Math.floor(difference / (1000 * 60 * 60 * 24));
      const hours = Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((difference % (1000 * 60)) / 1000);

      setTimeRemaining({ days, hours, minutes, seconds });
    }, 1000);

    return () => clearInterval(interval);
  }, [beneficiario]);

  const fetchBeneficiario = async () => {
    try {
      const response = await api.get(`/cliente/${cedula}`);
      const data = response.data;
      
      if (data.estado !== 'aprobado' && !data.wizard_completado) {
        navigate(`/cliente/${cedula}`);
        return;
      }
      
      setBeneficiario(data);
    } catch (error) {
      toast.error('Error al cargar información');
      navigate('/');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#1A1A1A] flex items-center justify-center">
        <div className="text-[#F5A623] font-heading text-xl">Cargando...</div>
      </div>
    );
  }

  const isDelivered = timeRemaining.days === 0 && timeRemaining.hours === 0 && 
                      timeRemaining.minutes === 0 && timeRemaining.seconds === 0;

  return (
    <div className="min-h-screen bg-[#1A1A1A] py-12 px-6 relative overflow-hidden">
      {/* Confetti Permanente */}
      <Confetti recycle={true} numberOfPieces={200} />
      
      {/* Glowing Orbs Background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <motion.div
          animate={{
            scale: [1, 1.3, 1],
            x: [0, 100, 0],
            y: [0, -50, 0],
          }}
          transition={{ duration: 15, repeat: Infinity, ease: "easeInOut" }}
          className="absolute top-1/4 right-1/4 w-[600px] h-[600px] bg-[#fbb040] rounded-full blur-[150px] opacity-20"
        />
        
        <motion.div
          animate={{
            scale: [1, 1.5, 1],
            x: [0, -100, 0],
            y: [0, 50, 0],
          }}
          transition={{ duration: 20, repeat: Infinity, ease: "easeInOut" }}
          className="absolute bottom-1/4 left-1/4 w-[500px] h-[500px] bg-[#10B981] rounded-full blur-[120px] opacity-30"
        />
      </div>
      
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        className="max-w-3xl mx-auto relative z-10"
      >
        {/* Kompaisito - Ajustado más abajo y más pequeño */}
        <motion.div
          className="absolute top-32 -right-24 hidden lg:block"
          animate={{ 
            y: [0, -20, 0],
          }}
          transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
        >
          <img
            src="https://customer-assets.emergentagent.com/job_f2ee6b04-88a4-4dce-acdb-9e470155c901/artifacts/sqmsw1b6_edited-photo%282%29.png"
            alt="Kompaisito"
            className="w-[150px] h-[150px] drop-shadow-2xl"
            style={{ transform: 'scaleY(2.778)' }}
          />
        </motion.div>
        
        <div className="text-center mb-12">
          {/* Copa con Zoom Animado */}
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: [1, 1.15, 1] }}
            transition={{ 
              scale: { duration: 2, repeat: Infinity, ease: "easeInOut" }
            }}
            className="w-32 h-32 mx-auto mb-6 flex items-center justify-center gold-glow"
          >
            <Trophy className="w-20 h-20 text-[#F5A623]" />
          </motion.div>
          
          {/* Título con Animación igual a Landing */}
          <motion.h1 
            className="text-5xl md:text-6xl font-black mb-4"
            animate={{ 
              color: ['#ffffff', '#fbb040', '#ffffff'],
            }}
            transition={{ duration: 3, repeat: Infinity }}
          >
            ¡FELICIDADES!
          </motion.h1>
          <p className="text-2xl md:text-3xl text-white mb-2 font-semibold">
            ERES UN GUERRERO
          </p>
          <p className="text-lg text-[#666666]">
            Tu meta ha sido alcanzada
          </p>
        </div>

        <div className="glass-card rounded-2xl p-8 mb-8 relative z-10" data-testid="countdown-section">
          <h2 className="text-xl text-white font-bold mb-6 text-center">
            Tiempo para gestión legal de Entrega
          </h2>
          
          {/* Contador Regresivo */}
          <div className="mb-6">
            <div className="grid grid-cols-4 gap-4 mb-4">
              {/* Días */}
              <motion.div
                className="bg-[#2A2A2A] border-2 border-[#F5A623] rounded-xl p-4 text-center"
                animate={{ 
                  borderColor: ['#F5A623', '#fbb040', '#F5A623'],
                }}
                transition={{ duration: 2, repeat: Infinity }}
              >
                <div className="text-4xl md:text-5xl font-black text-[#F5A623] mb-2">
                  {timeRemaining.days}
                </div>
                <div className="text-sm text-[#666666] uppercase tracking-wider">
                  Días
                </div>
              </motion.div>

              {/* Horas */}
              <motion.div
                className="bg-[#2A2A2A] border-2 border-[#F5A623] rounded-xl p-4 text-center"
                animate={{ 
                  borderColor: ['#F5A623', '#fbb040', '#F5A623'],
                }}
                transition={{ duration: 2, repeat: Infinity, delay: 0.2 }}
              >
                <div className="text-4xl md:text-5xl font-black text-[#F5A623] mb-2">
                  {timeRemaining.hours}
                </div>
                <div className="text-sm text-[#666666] uppercase tracking-wider">
                  Horas
                </div>
              </motion.div>

              {/* Minutos */}
              <motion.div
                className="bg-[#2A2A2A] border-2 border-[#F5A623] rounded-xl p-4 text-center"
                animate={{ 
                  borderColor: ['#F5A623', '#fbb040', '#F5A623'],
                }}
                transition={{ duration: 2, repeat: Infinity, delay: 0.4 }}
              >
                <div className="text-4xl md:text-5xl font-black text-[#F5A623] mb-2">
                  {timeRemaining.minutes}
                </div>
                <div className="text-sm text-[#666666] uppercase tracking-wider">
                  Minutos
                </div>
              </motion.div>

              {/* Segundos */}
              <motion.div
                className="bg-[#2A2A2A] border-2 border-[#F5A623] rounded-xl p-4 text-center"
                animate={{ 
                  borderColor: ['#F5A623', '#fbb040', '#F5A623'],
                }}
                transition={{ duration: 2, repeat: Infinity, delay: 0.6 }}
              >
                <div className="text-4xl md:text-5xl font-black text-[#F5A623] mb-2">
                  {timeRemaining.seconds}
                </div>
                <div className="text-sm text-[#666666] uppercase tracking-wider">
                  Segundos
                </div>
              </motion.div>
            </div>

            {beneficiario?.fecha_entrega && (
              <p className="text-center text-[#666666] text-sm">
                Fecha de entrega estimada: {new Date(beneficiario.fecha_entrega).toLocaleDateString('es-ES', {
                  day: 'numeric',
                  month: 'long',
                  year: 'numeric'
                })}
              </p>
            )}
          </div>

          {/* Card Verde "META CUMPLIDA" - Siempre visible */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="p-6 bg-gradient-to-r from-[#10B981]/20 to-[#10B981]/10 border-2 border-[#10B981] rounded-xl text-center"
          >
            <p className="text-2xl text-[#10B981] font-bold mb-2">
              ¡META CUMPLIDA! 🏁
            </p>
            <p className="text-white">
              Tu vehículo se está gestionando para entrega.
            </p>
            <p className="text-sm text-[#666666] mt-3 italic">
              Modelo de moto sujeto a cambio: Entregamos en base a disponibilidad en el mercado
            </p>
          </motion.div>
        </div>

        {/* Fecha de Entrega Super Grande */}
        {beneficiario?.fecha_entrega && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.5, duration: 0.6 }}
            className="text-center mb-12 relative z-10"
          >
            <motion.div
              animate={{ 
                boxShadow: [
                  '0 0 40px 20px rgba(245,166,35,0.3)',
                  '0 0 80px 40px rgba(255,149,0,0.5)',
                  '0 0 40px 20px rgba(245,166,35,0.3)'
                ]
              }}
              transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
              className="inline-block glass-card rounded-3xl p-12 border-4 border-[#F5A623]"
            >
              <p className="text-[#F5A623] text-2xl font-bold mb-4 uppercase tracking-widest">
                FECHA PROYECTADA PARA ENTREGA
              </p>
              <motion.p 
                className="text-6xl md:text-7xl lg:text-8xl font-black text-transparent bg-clip-text bg-gradient-to-r from-[#fbb040] via-yellow-300 to-[#fbb040] animate-gradient mb-4"
                animate={{ 
                  scale: [1, 1.05, 1],
                }}
                transition={{ duration: 2, repeat: Infinity }}
              >
                {new Date(beneficiario.fecha_entrega).toLocaleDateString('es-ES', {
                  day: 'numeric',
                  month: 'long',
                  year: 'numeric'
                }).toUpperCase()}
              </motion.p>
              <motion.div
                animate={{ 
                  y: [0, -10, 0],
                }}
                transition={{ duration: 2, repeat: Infinity }}
                className="text-4xl"
              >
                🏁
              </motion.div>
            </motion.div>
          </motion.div>
        )}

        <div className="glass-card rounded-2xl p-8 mb-8 relative z-10">
          <h2 className="text-xl text-white font-bold mb-6">Resumen de tu Meta</h2>
          <div className="grid md:grid-cols-2 gap-4">
            {/* Modelo de Moto */}
            <div className="flex items-start gap-3 p-4 bg-[#2A2A2A] rounded-lg">
              <Bike className="w-6 h-6 text-[#F5A623] flex-shrink-0 mt-1" />
              <div>
                <p className="text-[#666666] text-sm mb-1">Modelo de Moto</p>
                <p className="text-white font-semibold text-lg">
                  {beneficiario?.datos_financieros?.modelo_moto || 'Por confirmar'}
                </p>
              </div>
            </div>
            
            {/* Fecha de Entrega */}
            {beneficiario?.fecha_entrega && (
              <div className="flex items-start gap-3 p-4 bg-[#2A2A2A] rounded-lg">
                <Calendar className="w-6 h-6 text-[#F5A623] flex-shrink-0 mt-1" />
                <div>
                  <p className="text-[#666666] text-sm mb-1">Fecha de Entrega</p>
                  <p className="text-white font-semibold text-lg">
                    {new Date(beneficiario.fecha_entrega).toLocaleDateString('es-ES', {
                      day: 'numeric',
                      month: 'long',
                      year: 'numeric'
                    })}
                  </p>
                </div>
              </div>
            )}
            
            {/* Precio Final */}
            <div className="flex items-start gap-3 p-4 bg-[#2A2A2A] rounded-lg">
              <div className="w-6 h-6 text-[#F5A623] flex-shrink-0 mt-1 font-bold text-lg">REF</div>
              <div>
                <p className="text-[#666666] text-sm mb-1">Precio Final</p>
                <p className="text-white font-semibold text-lg">
                  REF {beneficiario?.datos_financieros?.precio_total?.toFixed(2) || '0.00'}
                </p>
              </div>
            </div>
            
            {/* Aporte Realizado */}
            <div className="flex items-start gap-3 p-4 bg-[#2A2A2A] rounded-lg">
              <div className="w-6 h-6 text-[#10B981] flex-shrink-0 mt-1 font-bold text-lg">✓</div>
              <div>
                <p className="text-[#666666] text-sm mb-1">Tu Aporte</p>
                <p className="text-[#10B981] font-semibold text-lg">
                  REF {beneficiario?.datos_financieros?.aporte?.toFixed(2) || '0.00'}
                </p>
              </div>
            </div>
            
            {/* Deuda */}
            <div className="flex items-start gap-3 p-4 bg-[#2A2A2A] rounded-lg">
              <div className="w-6 h-6 text-[#F59E0B] flex-shrink-0 mt-1 font-bold text-lg">⏱</div>
              <div>
                <p className="text-[#666666] text-sm mb-1">Deuda</p>
                <p className="text-[#F59E0B] font-semibold text-lg">
                  REF {beneficiario?.datos_financieros?.restante?.toFixed(2) || '0.00'}
                </p>
              </div>
            </div>
            
            {/* Cuota Mensual */}
            <div className="flex items-start gap-3 p-4 bg-[#2A2A2A] rounded-lg">
              <div className="w-6 h-6 text-[#3B82F6] flex-shrink-0 mt-1 font-bold text-lg">📅</div>
              <div>
                <p className="text-[#666666] text-sm mb-1">Cuota Mensual</p>
                <p className="text-[#3B82F6] font-semibold text-lg">
                  REF {beneficiario?.datos_financieros?.valor_cuota?.toFixed(2) || '0.00'}
                </p>
              </div>
            </div>
            
            {/* Colores Seleccionados */}
            {beneficiario?.colores_seleccionados?.length > 0 && (
              <div className="flex items-start gap-3 p-4 bg-[#2A2A2A] rounded-lg md:col-span-2">
                <Palette className="w-6 h-6 text-[#F5A623] flex-shrink-0 mt-1" />
                <div className="flex-1">
                  <p className="text-[#666666] text-sm mb-2">Colores Seleccionados</p>
                  <div className="flex flex-wrap gap-2">
                    {beneficiario.colores_seleccionados.map((color, idx) => (
                      <span key={idx} className="px-3 py-1 bg-[#F5A623]/20 text-[#F5A623] border border-[#F5A623]/30 rounded-full text-xs font-bold">
                        {color}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            )}
            
            {/* Documentos Validados */}
            <div className="md:col-span-2 p-4 bg-[#2A2A2A] rounded-lg">
              <p className="text-[#666666] text-sm mb-3">Documentos Validados</p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                {beneficiario?.documentos?.filter(doc => doc.estado === 'validado').map((doc, idx) => (
                  <div key={idx} className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-[#10B981]" />
                    <span className="text-white text-sm">{doc.nombre}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="flex gap-4 justify-center">
          <GoldButton
            onClick={() => navigate('/')}
            variant="secondary"
            testId="return-home-button"
          >
            Volver al Inicio
          </GoldButton>
        </div>
      </motion.div>
    </div>
  );
}